import React, { useMemo, useState } from "react";

type LineChartProps = {
  data?: any[];   // pre-shaped series: [{ label, value, meta }]
  points?: any[]; // raw points: [{ y, ... }]
};

function LineChart({ data = [], points = [] }: LineChartProps) {
  const w = 520;
  const h = 200;
  const p = 24;

  // 1) Normalize incoming data into a series of { label, value, ... }
  const series = useMemo(() => {
    if (data.length) return data;
    if (!points.length) return [];
    return points.map((pt: any, i: number) => ({
      label: `P${i + 1}`,
      value: pt.y ?? 0,
    }));
  }, [data, points]);

  const [hoverIndex, setHoverIndex] = useState<number>(() =>
    Math.max(0, series.length - 1)
  );

  // 2) Derived values
  const hasSeries = series.length > 0;
  const maxValue =
    (hasSeries &&
      Math.max(
        ...series.map((s: any) => (s.value != null ? s.value : 0))
      )) || 1;

  type CoordPoint = any & { x: number; y: number; index: number };

  const coords: CoordPoint[] = hasSeries
    ? series.map((s: any, i: number) => {
        const xNorm = series.length === 1 ? 0.5 : i / (series.length - 1);
        const yNorm = (s.value || 0) / maxValue;
        const x = p + xNorm * (w - 2 * p);
        const y = h - p - yNorm * (h - 2 * p);
        return { ...s, x, y, index: i };
      })
    : [];

  const clampedIndex =
    hasSeries && coords.length ? Math.min(hoverIndex, coords.length - 1) : 0;
  const hovered: CoordPoint | null = hasSeries ? coords[clampedIndex] : null;

  if (!hasSeries) {
    return (
      <div className="w-full h-[200px] flex items-center justify-center text-[11px] text-slate-400">
        No data
      </div>
    );
  }

  const path = coords
    .map((pt, i) => `${i ? "L" : "M"}${pt.x},${pt.y}`)
    .join(" ");
  const area = `${path} L ${
    coords[coords.length - 1].x
  },${h - p} L ${coords[0].x},${h - p} Z`;

  const tooltipStyle: React.CSSProperties | undefined = hovered
    ? {
        left: `${(hovered.x / w) * 100}%`,
        top: `${(hovered.y / h) * 100}%`,
        transform: "translate(-50%, -100%) translateY(-8px)",
      }
    : undefined;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[200px]">
        <defs>
          <linearGradient id="commitmentsArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect
          x="0"
          y="0"
          width={w}
          height={h}
          rx="12"
          className="fill-white"
        />

        {/* grid */}
        {Array.from({ length: 3 }).map((_, i) => {
          const y = p + ((h - 2 * p) / 3) * i;
          return (
            <line
              key={i}
              x1={p}
              x2={w - p}
              y1={y}
              y2={y}
              className="stroke-slate-100"
            />
          );
        })}

        {/* area + line */}
        <path d={area} fill="url(#commitmentsArea)" />
        <path
          d={path}
          className="stroke-indigo-600"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />

        {/* points + labels */}
        {coords.map((pt, i) => (
          <g
            key={i}
            className="cursor-pointer"
            onMouseEnter={() => setHoverIndex(i)}
          >
            <circle
              cx={pt.x}
              cy={pt.y}
              r={clampedIndex === i ? 4.5 : 3.5}
              className={
                clampedIndex === i
                  ? "fill-indigo-600 stroke-white"
                  : "fill-white stroke-indigo-600"
              }
              strokeWidth="2"
            />
            {/* x-axis labels */}
            {/* <text
              x={pt.x}
              y={h - p + 14}
              textAnchor="middle"
              className="text-[10px] fill-slate-500"
            >
              {pt.label}
            </text> */}
          </g>
        ))}
      </svg>

      {/* tooltip */}
      {hovered && (
        <div
          className="absolute z-10 rounded-xl bg-white/95 border border-slate-100 px-3 py-2 shadow-sm text-[11px] pointer-events-none"
          style={tooltipStyle}
        >
          <div className="font-semibold text-slate-800">
            {hovered.label || "Period"}
          </div>
          <div className="mt-0.5 text-slate-700">
            ${hovered.value?.toLocaleString() ?? 0}
          </div>
          {hovered.meta?.activeCount != null && (
            <div className="mt-0.5 text-slate-400">
              {hovered.meta.activeCount} active deal
              {hovered.meta.activeCount === 1 ? "" : "s"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LineChart;
